import type { PerceptionChannelState, PerceptionFusion } from "@/lib/api";

export const PERCEPTION_CHANNELS: Array<{
  key: keyof PerceptionFusion["channels"];
  short: string;
  name: string;
}> = [
  { key: "language", short: "L", name: "Language" },
  { key: "voice", short: "V", name: "Voice" },
  { key: "video", short: "Vid", name: "Video" },
];

export const CHANNEL_STATE_LABEL: Record<PerceptionChannelState, string> = {
  ready: "Ready",
  pending: "Pending",
  failed: "Failed",
  none: "Not available",
};

export function fusionTooltip(fusion: PerceptionFusion): string {
  const channels = PERCEPTION_CHANNELS.map(
    (c) => `${c.name}: ${CHANNEL_STATE_LABEL[fusion.channels[c.key]].toLowerCase()}`,
  );
  return [...channels, ...fusion.reasons].join("\n");
}

/** Shape only: SER valence scale differs by model, so the line is min/max normalized. */
export function VoiceSparkline({ values }: { values: number[] }) {
  if (values.length < 2) return null;
  const w = 64;
  const h = 18;
  const min = Math.min(...values);
  const span = Math.max(...values) - min || 1;
  const points = values
    .map((v, i) => `${((i / (values.length - 1)) * w).toFixed(1)},${(h - 2 - ((v - min) / span) * (h - 4)).toFixed(1)}`)
    .join(" ");
  return (
    <svg className="voice-spark" width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      <polyline points={points} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

export function PerceptionChannelsStrip({
  fusion,
  language,
  voice,
  video,
}: {
  fusion: PerceptionFusion;
  language: string;
  voice: { values: number[]; note: string };
  video: string;
}) {
  const rows: Array<{ key: keyof PerceptionFusion["channels"]; text: string; spark?: number[] }> = [
    { key: "language", text: language },
    { key: "voice", text: voice.note, spark: voice.values },
    { key: "video", text: video },
  ];
  return (
    <div className="channel-strip">
      {rows.map((row) => {
        const meta = PERCEPTION_CHANNELS.find((c) => c.key === row.key)!;
        const state = fusion.channels[row.key];
        return (
          <div key={row.key} className="channel-row">
            <span className={`channel-dot ${state}`} title={CHANNEL_STATE_LABEL[state]}>
              <i aria-hidden="true" />
              {meta.name}
            </span>
            <span className="channel-row-body" title={row.text}>
              {row.spark ? <VoiceSparkline values={row.spark} /> : null}
              <span>{row.text}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function PerceptionChannelDots({ fusion }: { fusion: PerceptionFusion }) {
  return (
    <span className="channel-dots">
      {PERCEPTION_CHANNELS.map((c) => {
        const state = fusion.channels[c.key];
        return (
          <span
            key={c.key}
            className={`channel-dot ${state}`}
            aria-label={`${c.name}: ${CHANNEL_STATE_LABEL[state]}`}
          >
            <i aria-hidden="true" />
            {c.short}
          </span>
        );
      })}
    </span>
  );
}
